import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  Divider, Chip, Stack, CircularProgress
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
  PhoneAndroid as TotpIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// ── Password change ───────────────────────────────────────────────────────────
const PasswordSection = () => {
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [status, setStatus] = useState(null);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword)
      return setStatus({ type: 'error', msg: 'Les nouveaux mots de passe ne correspondent pas.' });
    try {
      await adminAPI.changePassword(formData.currentPassword, formData.newPassword);
      setStatus({ type: 'success', msg: 'Mot de passe mis à jour avec succès !' });
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setStatus({ type: 'error', msg: err.response?.data?.error || 'Erreur lors de la mise à jour.' });
    }
  };

  return (
    <Card sx={{ borderRadius: 3, mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <LockIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700}>Changer le mot de passe</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {status && <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>{status.msg}</Alert>}
        <form onSubmit={handleUpdate}>
          <TextField fullWidth label="Mot de passe actuel" type="password" margin="normal"
            value={formData.currentPassword}
            onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })} />
          <TextField fullWidth label="Nouveau mot de passe" type="password" margin="normal"
            value={formData.newPassword}
            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })} />
          <TextField fullWidth label="Confirmer le nouveau mot de passe" type="password" margin="normal"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} />
          <Button variant="contained" type="submit" sx={{ mt: 2 }}>
            Mettre à jour le mot de passe
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ── 2FA section ───────────────────────────────────────────────────────────────
const TwoFactorSection = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [msg, setMsg] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  useEffect(() => {
    adminAPI.get2FAStatus()
      .then(r => { setEnabled(r.data.enabled); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSetup = async () => {
    setActionLoading(true); setMsg(null);
    try {
      const r = await adminAPI.setup2FA();
      setSetupData(r.data);
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur de configuration' });
    } finally { setActionLoading(false); }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setActionLoading(true); setMsg(null);
    try {
      await adminAPI.verify2FA(code);
      setEnabled(true); setSetupData(null); setCode('');
      setMsg({ type: 'success', text: '2FA activé avec succès ! Votre compte admin est maintenant sécurisé.' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Code invalide' });
    } finally { setActionLoading(false); }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) return;
    setActionLoading(true); setMsg(null);
    try {
      await adminAPI.disable2FA(disableCode);
      setEnabled(false); setShowDisable(false); setDisableCode('');
      setMsg({ type: 'info', text: '2FA désactivé' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Code invalide' });
    } finally { setActionLoading(false); }
  };

  if (loading) return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: 3 }}><CircularProgress size={24} /></CardContent>
    </Card>
  );

  return (
    <Card sx={{ borderRadius: 3, border: enabled ? '2px solid #2ecc71' : '2px solid #e0e0e0' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon sx={{ color: enabled ? '#2ecc71' : 'text.secondary' }} />
            <Typography variant="h6" fontWeight={700}>Authentification à deux facteurs (2FA)</Typography>
          </Box>
          <Chip label={enabled ? 'Activé' : 'Désactivé'} color={enabled ? 'success' : 'default'}
            icon={enabled ? <CheckIcon /> : undefined} />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

        {enabled ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Votre compte admin est protégé par l'authentification à deux facteurs.
            </Alert>
            {!showDisable ? (
              <Button variant="outlined" color="error" onClick={() => setShowDisable(true)}>
                Désactiver le 2FA
              </Button>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Entrez votre code d'authentification pour désactiver le 2FA :
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Code 2FA" value={disableCode} size="small"
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ maxLength: 6, style: { letterSpacing: 6, fontSize: 18, textAlign: 'center' } }}
                    sx={{ width: 160 }}
                  />
                  <Button variant="contained" color="error" onClick={handleDisable}
                    disabled={disableCode.length !== 6 || actionLoading}>
                    {actionLoading ? <CircularProgress size={20} /> : 'Confirmer'}
                  </Button>
                  <Button variant="text" onClick={() => { setShowDisable(false); setDisableCode(''); }}>
                    Annuler
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        ) : setupData ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Étape 1 :</strong> Scannez ce QR code avec votre application (Google Authenticator, Authy…)
            </Typography>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" src={setupData.qrCode} alt="QR Code 2FA"
                sx={{ width: 200, height: 200, border: '1px solid #eee', borderRadius: 2 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Ou entrez ce code manuellement :
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mb: 2, wordBreak: 'break-all' }}>
              {setupData.secret}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              <strong>Étape 2 :</strong> Entrez le code à 6 chiffres affiché dans l'application
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Code de vérification" value={code} size="small"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, style: { letterSpacing: 6, fontSize: 18, textAlign: 'center' } }}
                sx={{ width: 160 }}
              />
              <Button variant="contained" color="success" onClick={handleVerify}
                disabled={code.length !== 6 || actionLoading}>
                {actionLoading ? <CircularProgress size={20} /> : 'Activer le 2FA'}
              </Button>
              <Button variant="text" onClick={() => { setSetupData(null); setCode(''); }}>Annuler</Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ajoutez une couche de sécurité supplémentaire à votre compte admin. Une fois activé,
              un code sera requis à chaque connexion.
            </Typography>
            <Button variant="contained" startIcon={<TotpIcon />} onClick={handleSetup} disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Configurer le 2FA'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const AdminSecurity = () => (
  <Box sx={{ p: 3, maxWidth: 640 }}>
    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
      🔒 Paramètres de Sécurité
    </Typography>
    <PasswordSection />
    <TwoFactorSection />
  </Box>
);

export default AdminSecurity;
